import React from 'react'
import { useTween } from 'react-use'
import useElementSize from 'hooks/useElementSize'

import { FluidBox } from 'styled/index.js'
import QuestionBox from './../ThemesPanel/QuestionBox'
import { QuestionWrapper } from './../AnswersPanel/AnswersPanel.styled'

import {
  Root,
  Chart,
  ChartInner,
  AxisLine,
  GuideLine,
  GuideText,
  OptionGroup,
  LegendBox,
  Legend,
} from './ChartPanel.styled.js'

const MARGIN_LEFT = 40
const MARGIN_RIGHT = 20
const MARGIN_BOTTOM = 60
const MARGIN_TOP = 10

const optionPaths = [
  'M.042 298.89s27.478 1.144 36.256-21.372 24.043-16.793 32.057-17.556 26.715-4.961 42.744-15.647 24.807-3.816 31.676-6.488 21.754-21.372 38.164-28.241 34.73-14.121 42.363-26.334 40.453-52.666 51.139-53.048 16.793 1.909 23.281-13.357 18.318-41.981 25.188-35.493 33.966 38.546 50.758 36.256 20.609-8.4 34.729-27.86 17.937 2.672 24.807.764 37.782-54.575 50.376-75.947 24.044-17.174 36.638 10.305 18.075 35.616 28.623 23.661c17.174-19.463 29.386 48.349 35.874 70.484',
  'M.032 298.969s27.478.891 36.256-16.643 24.043-13.077 32.057-13.671 26.715-3.863 42.744-12.185 24.807-2.972 31.676-5.052 21.754-16.643 38.164-21.992 34.73-11 42.363-20.507 40.453-41.012 51.139-41.309 16.793 1.487 23.281-10.4 18.318-32.691 25.188-27.639 33.966 30.016 50.758 28.233 20.609-6.538 34.729-21.7 17.937 2.081 24.807.595 37.782-42.5 50.376-59.141 24.044-13.374 36.638 8.025 18.078 27.735 28.626 18.429c17.174-15.156 29.386 37.65 35.874 54.887',
  'M.36 298.948s21.753-8.4 32.439-22.517 20.1-15.01 37.019-6.107c7.252 3.817 40.454-9.922 43.889-12.594s13.734-4.579 28.241-4.579c5.343 0 15.648-3.435 18.7-6.87s21.753-2.671 27.1 0 14.121-.382 23.28-9.159 16.029-2.672 25.189-6.87 20.227-18.7 29-31.294 16.029-2.29 22.517-7.633 22.517-43.126 28.623-38.927 14.5 30.149 22.9 36.637 37.019 24.807 48.85 22.9 16.792-6.106 23.28-10.3 11.449.382 18.319-3.053 19.463-17.937 25.951-28.623 15.157-14.937 29.008-14.127c6.488.382 10.3-9.922 17.555-10.3s12.836 2.145 24.044 17.555c6.106 8.4 13.538 14.989 18.318 6.488 3.435-6.106 14.282-34.546 30.15 20.991 7.633 26.714 10.3 33.118 10.3 33.118',
]

const optionsProps = {
  width: 585.645,
  height: 300,
  range: 53.14,
  y: [120, 160, 224],
}

const calcOptionPct = (result, optionId, total) => {
  const resultOption = result.filter(r => r.optionId === optionId)

  if (resultOption.length === 0) {
    return 0
  } else {
    return resultOption[0].count / total
  }
}

const LegendItem = ({
  option,
  optionPct,
  optionId,
  userAnswer,
  question,
  entering,
  leaving,
  navDirection,
}) => {
  const tween = useTween('linear', 700, 1200)

  return (
    <Legend
      entering={entering}
      leaving={leaving}
      nth={optionId}
      navDirection={navDirection}
      px="spaceSmall"
      color={
        question.correct === optionId ? 'accent' : userAnswer === optionId ? 'wrong' : 'primary'
      }
    >
      {question.correct === optionId && (
        <FluidBox fw="light" fs="textSmall" mb="spaceXs">
          Correct answer
        </FluidBox>
      )}
      <FluidBox fw="bold" color="body">
        {`${(100 * optionPct * tween).toFixed(1)}% of people answered`}
      </FluidBox>
      <FluidBox fs={[18, 18, 18, 18]} fw="bold" py="spaceXs">
        {option}
      </FluidBox>
      {userAnswer === optionId && (
        <FluidBox fw="light" fs="textSmall">
          This was your answer
        </FluidBox>
      )}
    </Legend>
  )
}
const ChartPanel = ({ result, question, userAnswer, entering, leaving, navDirection }) => {
  const [innerRef, { width, height }] = useElementSize()
  const { xAxis, yAxis, yDomain } = question.chartConfig

  const chartW = width - MARGIN_LEFT - MARGIN_RIGHT
  const chartH = height - MARGIN_BOTTOM - MARGIN_TOP

  const axisScaleX = chartW / (xAxis.length - 1)
  const axisScaleY = chartH / (yAxis.length - 1)

  const optionsScale = {
    x: (chartW - 1 * axisScaleX) / optionsProps.width,
    y: optionsProps.range / optionsProps.height / (yDomain.range / chartH),
  }
  const valueScale = chartH / yDomain.range

  const optionsTransform = {
    x: axisScaleX * 1,
    y: (yDomain.range - optionsProps.range) * valueScale,
  }

  const totalAnswers = result.reduce((total, res) => {
    total += res.count
    return total
  }, 0)

  return (
    <Root py="spaceNormal">
      <div className="Inner">
        <QuestionWrapper entering={entering} leaving={leaving} navDirection={navDirection}>
          <QuestionBox
            question={question}
            selected={true}
            correct={userAnswer === question.correct}
            selectedTheme={true}
          ></QuestionBox>
        </QuestionWrapper>
        <Chart mb="spaceLarge" entering={entering} leaving={leaving} navDirection={navDirection}>
          <ChartInner ref={innerRef}>
            <svg viewBox={`0 0 ${width} ${height}`}>
              <g transform={`translate(${MARGIN_LEFT}, ${MARGIN_TOP})`}>
                <g className="Guides">
                  {xAxis.map(
                    (xGuide, id) =>
                      id < xAxis.length && (
                        <g
                          key={`xGuide_${id}`}
                          transform={`translate(${axisScaleX * id}, ${chartH})`}
                        >
                          <GuideText textAnchor="middle" alignmentBaseline="hanging" y={10}>
                            {xGuide.split(' ').map((ts, id) => (
                              <tspan key={id} x={0} dy={10 * (id + 1)} alignmentBaseline="hanging">
                                {ts}
                              </tspan>
                            ))}
                          </GuideText>
                          {id !== 0 && <GuideLine d={`M0 0v${-chartH}`}></GuideLine>}
                        </g>
                      )
                  )}
                  {yAxis.map((yGuide, id) => (
                    <g key={`yGuide_${id}`} transform={`translate(0, ${chartH - axisScaleY * id})`}>
                      <GuideText textAnchor="end" alignmentBaseline="middle" x={-10}>
                        {yGuide}
                      </GuideText>
                      {id < yAxis.length - 1 && <GuideLine d={`M0 0h${chartW}`}></GuideLine>})
                    </g>
                  ))}
                </g>
                <g className="Axis">
                  <AxisLine d={`M0 ${chartH}h${chartW}`}></AxisLine>
                  {/* <AxisLine d={`M0 0v${chartH}`}></AxisLine> */}
                </g>
                <g
                  transform={`translate(${optionsTransform.x}, ${optionsTransform.y}) scale(${optionsScale.x}, ${optionsScale.y})`}
                >
                  {question.options.map((option, id) => (
                    <OptionGroup
                      key={id}
                      color={
                        question.correct === id ? 'accent' : userAnswer === id ? 'wrong' : 'primary'
                      }
                    >
                      <path d={optionPaths[id]}></path>
                      <g
                        transform={`scale(${1 / optionsScale.x}, ${1 /
                          optionsScale.y}) translate(${optionsProps.width *
                          optionsScale.x}, ${optionsProps.y[id] * optionsScale.y} )`}
                      >
                        <circle r={4}></circle>
                        <text textAnchor="start">
                          <tspan x={0} dx={10} dy={0} alignmentBaseline="central">
                            {id + 1}
                          </tspan>
                        </text>
                      </g>
                    </OptionGroup>
                  ))}
                </g>
              </g>
            </svg>
          </ChartInner>
        </Chart>
        <LegendBox>
          {question.options.map((option, id) => (
            <LegendItem
              key={id}
              entering={entering}
              leaving={leaving}
              navDirection={navDirection}
              optionPct={calcOptionPct(result, id, totalAnswers)}
              optionId={id}
              userAnswer={userAnswer}
              option={option}
              question={question}
            ></LegendItem>
          ))}
        </LegendBox>
      </div>
    </Root>
  )
}

export default ChartPanel
